import {QueryClientProvider} from '@tanstack/react-query'
import arrayMutators from 'final-form-arrays'
import {ThemeProvider} from 'quickstart/blocks/ThemeBlock/branding'
import {
  BaseGlobalStyle,
  CssModulesGlobalStyle,
  LinariaGlobalStyle,
  StyledComponentsGlobalStyle,
} from 'quickstart/blocks/ThemeBlock/globalStyles'
import type {
  AdminInnerWrapperProps,
  AdminWrapperProps,
  GlobalMigrateWrapper,
  MigrateWrapper,
} from 'quickstart/types'
import {tid} from 'quickstart/utils'
import {useCallback, useRef, useState} from 'react'
import * as Final from 'react-final-form'
import {logger, stableJson, tryJson} from 'tizra'
import {AdminContextProvider, fromServer} from '../base/hooks/useAdminContext'
import * as S from './styles'
import {HydrationProvider} from 'quickstart/hooks'

const log = logger('admin/renderer')

export const AdminInnerWrapper = ({
  children,
  global,
  context,
  queryClient,
}: AdminInnerWrapperProps) => (
  <HydrationProvider hydrate={false}>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider context={context}>
        <BaseGlobalStyle />
        <CssModulesGlobalStyle />
        <LinariaGlobalStyle />
        <StyledComponentsGlobalStyle />
        <AdminContextProvider context={context} global={global}>
          {/* reskin is read off the server context rather than through
           * useReskin(), because this component renders the provider that
           * hook consumes. */}
          <S.AdminWrapper $reskin={context.reskin} {...tid('admin')}>
            {children}
          </S.AdminWrapper>
        </AdminContextProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </HydrationProvider>
)

export function AdminWrapper({
  component: Admin,
  global = false,
  context,
  migrate,
  name,
  queryClient,
  textarea,
}: AdminWrapperProps) {
  const [config] = useState(() => {
    if (textarea.value.trim().length !== 0) {
      const tv = tryJson<any>(textarea.value, e => log.error(e))
      return global ? tv?.[name] : tv
    }
  })

  const [initialValues] = useState(() =>
    global
      ? (migrate as GlobalMigrateWrapper<any>)({
          adminContext: fromServer(context, global),
          globalConfig: config,
        })
      : (migrate as MigrateWrapper<any>)({
          adminContext: fromServer(context, global),
          config,
        }),
  )

  const isFirstChange = useRef(true)
  const handleChange = useCallback(
    ({values}: {values: any}) => {
      const tv = global
        ? {
            ...tryJson<any>(textarea.value),
            [name]: values,
          }
        : values
      textarea.value = stableJson(tv, {indent: 2})
      // A global config's textarea is one of the admin's own tracked fields: it
      // sits in the screen's `#f` form carrying `ftest` beside an `-h` twin, and
      // it is the unit of change for this whole form -- the individual fields we
      // render are removed before submit, so they are not fields of record and
      // must not be tracked themselves. Writing the textarea in code fires no
      // event, so nothing re-runs the comparison, and without this the admin's
      // Save stays disarmed and its navigate-away warning never appears while
      // real edits sit in the form. The admin's own controls arm it the same way
      // (`pickerCombobox`, `t-color`).
      //
      // Block configs are deliberately excluded. Their textarea lives in the
      // dialog's own `form-layout-block-properties`, not in `#f`, carries no
      // `ftest` and no twin, and is discarded wholesale when the dialog closes
      // without OK -- so arming `#f` here would warn about edits that Cancel
      // correctly threw away.
      //
      // `FormSpy` also calls this on its initial subscription, before any
      // edit -- `migrate` can turn an empty stored config into non-empty
      // defaults, so the textarea above is written on mount even though
      // nothing has changed yet. Reporting that first notification would
      // compare the migrated JSON against the as-loaded `-h` twin and mark
      // the screen dirty merely by opening it, so only the mirror above runs
      // unconditionally; the report itself waits for a real edit.
      if (global && !isFirstChange.current) {
        ;(window as any).turnOnChange?.('#f .ftest', '#f .ftarget', true)
      }
      isFirstChange.current = false
    },
    [global, name, textarea],
  )

  return (
    <Final.Form
      onSubmit={() => {
        log.warn('FinalForm submitted, whoops?')
      }}
      initialValues={initialValues}
      mutators={{...arrayMutators}}
      render={({handleSubmit, ...renderProps}) => (
        <form onSubmit={handleSubmit}>
          <Final.FormSpy onChange={handleChange} />
          <AdminInnerWrapper
            context={context}
            global={global}
            queryClient={queryClient}
          >
            <Admin {...renderProps} />
          </AdminInnerWrapper>
        </form>
      )}
    />
  )
}
